/* U Press multi-sheet ordering for Legacy Prints.  HARDENED.
   =========================================================================

   WHY ANY OF THIS EXISTS
   BAGS adds the cart line and then sends the customer to /cart, so ordering a
   second sheet means navigating back and starting over. Measured on the wire:

       +9ms     POST /cart/add.js      script-initiated, 436-byte body
       +1007ms  200  /cart/add.js      the line item now exists
       +1008ms  GET  /cart.json        BAGS re-reads the cart
       +1009ms  navigation to /cart    reason=scriptInitiated
       +1015ms  navigated

   The add is COMPLETE and independent of the navigation, so we never touch
   BAGS' cart submission. We cancel only the navigation, with the Navigation
   API's `navigate` event (Baseline Jan 2026: Chrome, Edge, Firefox 147,
   Safari 26.2).

   SAFE FAILURE
   If the Navigation API is missing, or the event is not cancelable, or anything
   in here throws, we intercept nothing and the customer gets BAGS' original
   behaviour: they land on /cart with a correctly added item. A failure of the
   redirect suppression can never prevent ordering.

   THE BUTTON RE-ENABLE, AND WHY IT IS NEEDED
   BAGS disables its own cart button after a successful add, because in its flow
   the customer leaves and never returns. Measured: after using BAGS' OWN remove
   control and uploading a fresh file with a completed upload and a valid size
   match, BAGS left the button disabled for 48 seconds and counting. No BAGS
   action, event or control restores it. So we restore it - but only when BAGS
   is observably back in the same state it is in when BAGS itself enables the
   button on a fresh page. Those conditions were captured from a real fresh-page
   upload (bags_state.py) and are enumerated in bagsReady().
*/
(function () {
  'use strict';

  var CFG = (window.upOrderConfig) || {};
  if (CFG.enabled === false) return;

  /* ==================================================================== *
   *  BAGS ADAPTER - the ONLY part of this file that knows what the vendor  *
   *  app looks like. Everything below the closing marker is our own flow   *
   *  and would survive the vendor changing its markup.                     *
   *                                                                        *
   *  WHY IT IS SHAPED THIS WAY                                             *
   *  The vendor owns upload, DPI, size matching, pricing, the cart payload  *
   *  and production routing. We do not modify it, patch it, or call into    *
   *  its internals. So the only way to know it has finished with a sheet is  *
   *  to read what it has rendered. Every probe below is a READ.             *
   *                                                                        *
   *  FAIL-CLOSED, AND WHAT THAT COSTS                                       *
   *  If the vendor reworded any signal - "Upload Complete 100%",            *
   *  "SIZE MATCHED", "Variant", a price, "File DPI", a NN" x NN" pair - the  *
   *  matching probe goes dark and bagsReady() returns false forever.        *
   *                                                                        *
   *  What that does, precisely:                                            *
   *    - Sheet 1 is UNAFFECTED. We never touch the button there; the vendor  *
   *      enables its own and the customer orders normally.                  *
   *    - Sheet 2+ degrades to the blocker this pass fixed: the button stays  *
   *      as the vendor left it and the customer must reload to order again.  *
   *    - We never place a WRONG order. Readiness only ever gates whether we  *
   *      restore a button; the vendor still decides what goes in the cart,   *
   *      and success is only ever shown when the expected line is observed   *
   *      in /cart.js.                                                       *
   *                                                                        *
   *  That trade is deliberate: a stuck button is recoverable by reloading,  *
   *  a wrongly-priced or wrongly-routed order is not. Two conditions were   *
   *  REMOVED after measuring that the vendor does not require them - a      *
   *  preview image, and a file in the input - because each made us stricter  *
   *  than the vendor and produced exactly that stuck button on real files.  *
   *                                                                        *
   *  DIAGNOSING A VENDOR CHANGE                                            *
   *  window.upOrder.adapter() names every probe and whether it currently    *
   *  matches. A probe reading false across several different real sheets is  *
   *  the signal that the vendor's wording moved.                            *
   * ==================================================================== */
  var SEL = {
    cartButton: '#gs-add-cart-btn, .gs-sd-cart-btn',
    removeButton: '.gs-file-remove',
    wrapper: '.gs-uploader-wrapper',
    host: '.gs--wrapper',
    fileInput: '#gs-file-input',
    detection: '.gs-sd-detection-result',
    card: '.gs-file-container, .gs-sd-upload-card',
    quantity: '#gs-quantity, input[name="quantity"]'
  };

  var COPY = {
    relabel: CFG.cartButtonLabel || 'Add sheet to order',
    adding: CFG.addingLabel || 'Adding sheet…',
    addedShort: CFG.addedLabel || 'Sheet added',
    added: 'Sheet added to your order',
    preparing: CFG.preparingLabel || 'Preparing your sheet…',
    stillPreparing: 'Your sheet is still uploading. This button will be ready in a moment.',
    another: 'Add another sheet',
    view: 'View order',
    keep: 'Continue shopping',
    summaryOne: '1 sheet',
    summaryMany: '{n} sheets',
    yourOrder: 'Your order',
    unconfirmed: 'We couldn’t confirm this sheet from here.',
    unconfirmedMeta: 'It may still have been added — please check your order before ordering it again.'
  };

  /* ---------------------------------------------------------------- state
     ready -> attempting -> waiting -> success
                                   \-> ready again, as soon as we can tell the
                                       add did not happen (never a blind wait) */
  var S = { READY: 'ready', ATTEMPTING: 'attempting', WAITING: 'waiting',
            SUCCESS: 'success' };
  var state = S.READY;
  var DEBOUNCE_MS = 600;        // collapses a physical double interaction only
  var REFUSAL_PROBE_MS = 3500;  // a successful add has reached the cart by now
  var POLL_MAX_MS = 30000;

  var guard = { supported: false, armed: false, blocked: 0, allowNext: false,
                failures: 0, grace: false };
  var lastAdded = null;   // fingerprint of the sheet we last put in the cart
  var expected = null;    // what we are waiting for the cart to show
  var successEl = null, summaryEl = null;

  function log() {
    if (!CFG.debug) return;
    try { console.log.apply(console, ['[up-order]', state].concat([].slice.call(arguments))); }
    catch (e) {}
  }

  /* ------------------------------------------------------------ the guard */
  try {
    guard.supported = !!(window.navigation && window.navigation.addEventListener);
  } catch (e) { guard.supported = false; }

  if (guard.supported) {
    try {
      navigation.addEventListener('navigate', function (e) {
        try {
          if (!guard.armed || guard.allowNext) return;
          var path = new URL(e.destination.url).pathname;
          if (path !== '/cart') return;
          /* Inside the post-success grace window we only suppress navigations
             the PAGE started. If the customer themselves clicks through to
             their cart in that moment, that is exactly what they asked for and
             it must not be swallowed. */
          if (guard.grace && e.userInitiated) {
            log('customer asked for /cart during the grace window; allowing');
            return;
          }
          if (!e.cancelable) {
            guard.failures++;
            log('cart navigation not cancelable; letting BAGS proceed');
            return;
          }
          e.preventDefault();
          guard.blocked++;
          log('suppressed the redirect to /cart');
        } catch (err) {
          /* Anything unexpected must NOT break ordering. Doing nothing means
             BAGS behaves exactly as it does today. */
          guard.failures++;
          log('guard error, falling back to BAGS behaviour', err && err.message);
        }
      });
    } catch (e) {
      guard.supported = false;
      log('could not attach the navigate listener; BAGS behaviour unchanged');
    }
  } else {
    log('Navigation API unavailable; BAGS behaviour unchanged');
  }

  /* Armed only while an add of ours is genuinely in flight. The 12s timer used
     to disarm unconditionally while poll() was still running to 30s - so a slow
     BAGS response, which is precisely when the redirect fires, would send the
     customer to /cart after all. Re-arming while we are still waiting closes
     that window, and it cannot run away: poll() always ends, and both of its
     exits (success and backToReady) disarm. */
  function arm() {
    guard.armed = true;
    clearTimeout(arm._t);
    arm._t = setTimeout(function () {
      if (state === S.ATTEMPTING || state === S.WAITING) { arm(); return; }
      disarm();
    }, 12000);
  }
  function disarm() {
    guard.armed = false;
    guard.grace = false;
    clearTimeout(disarmAfterSuccess._t);
  }

  /* BAGS fires its redirect at almost exactly the moment our /cart.js poll
     confirms the line. Measured across 12 consecutive adds, the redirect
     arrived 37-182ms BEFORE we reached success (median 92ms) - so disarming the
     instant we show success left a margin of tens of milliseconds. Any
     scheduling hiccup - a loaded machine, a slow phone, a congested network -
     reorders those two and the redirect escapes, which is the intermittent
     bounce to /cart.

     A redirect arriving just after success still belongs to the add we have
     just completed, so the guard stays armed briefly rather than switching off
     on the exact tick we win the race. The window is short, bounded, and inside
     it a customer-initiated navigation is always allowed through. */
  var GRACE_MS = 4000;

  function disarmAfterSuccess() {
    guard.grace = true;
    clearTimeout(disarmAfterSuccess._t);
    disarmAfterSuccess._t = setTimeout(function () {
      guard.grace = false;
      guard.armed = false;
      log('grace window closed');
    }, GRACE_MS);
  }

  /* -------------------------------------------------------- observing BAGS */
  function vis(e) { return !!e && e.offsetHeight > 0 && e.offsetWidth > 0; }
  function txt(e) { return e ? (e.innerText || '').replace(/\s+/g, ' ').trim() : ''; }

  /* A fingerprint of the sheet BAGS is currently holding. The preview src
     changes on every upload even when the same file is chosen again, so it is
     the reliable "BAGS really did a new upload" signal. */
  function bagsFingerprint() {
    var inp = document.querySelector(SEL.fileInput);
    var f = inp && inp.files && inp.files[0];
    var host = document.querySelector(SEL.host);
    var img = host ? host.querySelector('img') : null;
    return {
      name: f ? f.name : null,
      size: f ? f.size : null,
      modified: f ? (f.lastModified || 0) : null,
      preview: img ? String(img.currentSrc || img.src || '').slice(-64) : null,
      card: txt(document.querySelector(SEL.card)).slice(0, 80)
    };
  }

  /* BAGS gives us no id for an upload: there is no hidden field, no form
     property, and for very large files no preview either - so nothing in the
     DOM distinguishes one upload from the next. What we CAN observe is our own
     reset: addAnother() empties the file input, so the next non-empty state is
     necessarily a new selection. That is true even when the customer picks the
     same filename twice, which comparing name and size cannot tell apart. */
  var freshFile = true;     // the file now in the input has not been added yet
  var sawEmptyInput = true; // we have actually SEEN the input empty since then

  /* Upload generation. Advances whenever the customer chooses a sheet or starts
     another one. Every asynchronous continuation below captures it first and
     discards itself if the generation has moved on, so a slow /cart.js reply
     for a sheet the customer has already replaced can never paint over the
     current one. Without this the failure is quiet and wrong rather than loud:
     a stale summary, or a success card for the wrong sheet. */
  var sheetGen = 0;
  function bumpGen(how) { sheetGen++; log('sheet generation', sheetGen, how); }
  function currentGen() { return sheetGen; }
  function stale(gen) { return gen !== sheetGen; }

  /* A sheet arrives by one of two routes and only one of them touches the file
     input - BAGS' own code records that "a drag-and-drop never populates the
     input's FileList". Demanding a file in the input therefore made a DROPPED
     sheet permanently unready: the same blocker, reached by a different
     condition. What holds for both routes is BAGS' upload card naming a file. */
  var FILE_IN_CARD = /[.](png|jpe?g|webp|pdf|ai|eps|psd|svg|tiff?)/i;

  /* Every string the vendor renders that we depend on, defined ONCE. bagsReady()
     and adapterReport() both read this table, so the diagnostic can never drift
     away from the verdict it is meant to explain. */
  var PROBE = {
    uploadComplete: /100\s*%|complete/i,
    uploadFailed:   /fail|error/i,
    sizeMatched:    /size\s*match/i,
    variant:        /variant/i,
    price:          /\$[0-9]/,
    fileDpi:        /file\s*dpi\s*[0-9]/i,
    dimensions:     /[0-9.]+"\s*[x×]\s*[0-9.]+"/i
  };

  /* The Preparing state must not accept a click. Before this, the button was
     relabelled but still live, so a customer could click "Preparing your
     sheet…", have BAGS refuse it, and be left with nothing happening and no
     explanation.

     We do NOT set `disabled`. Writing that flag is how the vendor wedged the
     button in the first place, and if our readiness verdict were ever wrong we
     would have recreated exactly the blocker this pass fixed. Instead the click
     is held in the capture phase, the customer is told why - and if they insist,
     we get out of the way and let BAGS decide. Our gate can delay an order; it
     must never be able to prevent one. */
  var blockedClicks = 0;
  var BLOCKED_CLICK_LIMIT = 2;
  var liveRegion = null;

  function announce(msg) {
    try {
      if (!liveRegion) {
        liveRegion = document.createElement('p');
        liveRegion.className = 'up-ord__live';
        liveRegion.setAttribute('role', 'status');
        liveRegion.setAttribute('aria-live', 'polite');
        (document.querySelector(SEL.host) || document.body).appendChild(liveRegion);
      }
      liveRegion.textContent = '';
      setTimeout(function () { liveRegion.textContent = msg; }, 60);
    } catch (e) {}
  }

  function preparing() {
    var b = document.querySelector(SEL.cartButton);
    return !!b && b.getAttribute('data-up-state') === 'uploading';
  }

  function hasFile() {
    var inp = document.querySelector(SEL.fileInput);
    if (inp && inp.files && inp.files.length === 1) return true;
    var card = document.querySelector(SEL.card);
    return vis(card) && FILE_IN_CARD.test(txt(card));
  }

  /* Choosing a file is something we can watch directly, by either route. That
     is a stronger signal than inferring it from the input, and it is the only
     one a drop gives us at all. */
  function noteNewChoice(how) {
    if (state !== S.READY) return;
    /* The generation advances on EVERY choice, including the first sheet and
       including a customer who changes their mind before adding anything. The
       freshness flag is separate: it only has to flip once. Gating the bump on
       it left generation 0 covering every sheet-1 choice, so two rapid choices
       there were indistinguishable to anything waiting on an async result. */
    bumpGen('sheet chosen via ' + how);
    blockedClicks = 0;
    freshFile = true;
    ensureWatching();
  }

  /* A file only counts as new once we have watched the input go empty and then
     fill again. Assuming the reset worked would be enough on a good day; seeing
     it is what stops us calling a leftover sheet "new" if it ever does not. */
  function noteFileState() {
    var inp = document.querySelector(SEL.fileInput);
    var n = inp && inp.files ? inp.files.length : 0;
    if (n === 0) { sawEmptyInput = true; return; }
    if (n === 1 && !freshFile && sawEmptyInput && state === S.READY) freshFile = true;
  }

  /* bagsReady reads innerText, which forces layout, and the button is repainted
     on every BAGS mutation - so the verdict is memoised for a moment. */
  var readyMemo = { t: 0, v: null };
  function bagsReadyCached() {
    var now = Date.now();
    if (readyMemo.v && now - readyMemo.t < 150) return readyMemo.v;
    readyMemo = { t: now, v: bagsReady(false) };
    return readyMemo.v;
  }

  /* The conditions BAGS itself satisfies when IT enables the button, captured
     from a real fresh-page upload. We never enable on fewer than all of them. */
  function bagsReady(requireFresh) {
    var why = [];
    var btn = document.querySelector(SEL.cartButton);
    var inp = document.querySelector(SEL.fileInput);
    var card = document.querySelector(SEL.card);
    var det = document.querySelector(SEL.detection);
    var host = document.querySelector(SEL.host);
    var img = host ? host.querySelector('img') : null;

    if (!btn) why.push('no cart button');
    else if (!vis(btn)) why.push('cart button not visible');

    if (inp && inp.files && inp.files.length > 1) why.push('more than one file selected');
    else if (!hasFile()) why.push('no sheet loaded');

    var cardText = txt(card);
    if (!vis(card)) why.push('no upload card');
    else if (!PROBE.uploadComplete.test(cardText)) why.push('upload still processing');
    if (PROBE.uploadFailed.test(cardText)) why.push('upload card shows a failure');

    var d = txt(det);
    if (!vis(det) || !d) why.push('no size detection result');
    else {
      if (!PROBE.sizeMatched.test(d)) why.push('no size match');
      if (!PROBE.variant.test(d)) why.push('no variant');
      if (!PROBE.price.test(d)) why.push('no price');
      if (!PROBE.fileDpi.test(d)) why.push('no file DPI');
      if (!PROBE.dimensions.test(d)) why.push('no dimensions');
    }

    /* The preview is corroborating, never required. BAGS builds its thumbnail
       in the browser as a base64 data URI and that fails on very large sheets:
       a 6750 x 36300 file (245 MP) leaves .gs-sd-thumb-placeholder showing and
       no <img> at all - yet BAGS enables its own button and completes the add.
       Measured on a fresh page: BAGS ready at 23s, preview never. Requiring one
       was stricter than BAGS' own contract and left the button dead forever.
       So: if a preview is there it must have finished loading; if it is not
       there, the conditions above are what BAGS itself goes on. */
    if (img && vis(img) && !img.naturalWidth) why.push('preview still loading');

    if (host) {
      var errs = [].slice.call(host.querySelectorAll('[class*=error],[class*=invalid]'))
        .filter(vis).map(txt).filter(Boolean);
      if (errs.length) why.push('BAGS error visible: ' + errs[0].slice(0, 40));
    }

    if (requireFresh && !freshFile) {
      why.push('no new sheet has been chosen since the last one was added');
    }
    return { ok: why.length === 0, why: why };
  }

  /* Restore the button ONLY into a proven-fresh BAGS state.

     Readiness is a STATE, not a deadline. The previous version opened a
     90-second window at the moment the customer clicked "Add another sheet" -
     before they had even opened the file dialog - and on expiry cleared its own
     interval with no way back. A 73 MB upload can outlast that easily, and once
     the window closed BAGS' own post-add disable was permanent for the rest of
     the page's life. So this watcher has no deadline: it keeps evaluating while
     we are waiting for a sheet, and it stops itself the moment it enables one.

     It never disables anything. The only write is clearing a disabled flag BAGS
     set, and only once every condition BAGS itself satisfies is observed. */
  var reenableArmed = false;   // only an add of ours earns the right to re-enable

  function reenableWhenReady() {
    reenableArmed = true;
    ensureWatching();
    syncReadiness();
  }

  /* The watcher also keeps the BUTTON LABEL honest, which is why it runs for the
     first sheet too - there we never touch `disabled`, BAGS owns it. Without
     this the label is only repainted when BAGS mutates the DOM, so it freezes on
     "Preparing your sheet…" the moment BAGS goes quiet, on a button that is
     actually ready. It stops itself once ready, and once the uploader has been
     idle for a few seconds, so an untouched page is not polling forever. */
  function ensureWatching() {
    if (reenableWhenReady._alive) return;
    reenableWhenReady._alive = true;
    reenableWhenReady._idle = 0;
    clearInterval(reenableWhenReady._t);
    reenableWhenReady._t = setInterval(syncReadiness, 400);
  }

  function stopWatching() {
    reenableWhenReady._alive = false;
    clearInterval(reenableWhenReady._t);
  }

  function syncReadiness() {
    if (state !== S.READY) return;
    noteFileState();
    if (!hasFile()) {
      paintButton();
      if (++reenableWhenReady._idle > 12) stopWatching();
      return;
    }
    reenableWhenReady._idle = 0;
    var btn = document.querySelector(SEL.cartButton);
    if (!btn) return;
    /* This tick decides whether to stop watching, so it must not paint from a
       memo taken before BAGS settled - that would freeze the label on
       "Preparing your sheet…" over a button that is ready. */
    readyMemo = { t: 0, v: null };
    var r = bagsReady(true);
    reenableWhenReady._last = r;
    paintButton();
    if (!r.ok) return;
    if (reenableArmed && btn.disabled) {
      btn.disabled = false;
      btn.removeAttribute('aria-disabled');
      log('re-enabled the cart button; BAGS state verified');
    }
    reenableArmed = false;
    stopWatching();
  }

  /* =================== END OF THE BAGS ADAPTER ======================== *
   *  Everything below is our own ordering flow: the cart API, the state    *
   *  machine, the success card and the button. It reads the adapter's      *
   *  verdict and never the vendor's markup.                                *
   * ==================================================================== */

  /* --------------------------------------------------------------- cart --- */
  /* Shopify's cart API is the authority. If it answers with anything that is
     not JSON - which a preview session can do before a cart cookie exists - we
     treat it as a failure rather than guessing, and the customer is told plainly
     rather than left staring at a button that never changes. */
  var cartFailures = 0;
  function cart() {
    return fetch('/cart.js', { cache: 'no-store', credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('cart api ' + r.status);
        /* Shopify answers /cart.js with Content-Type: text/javascript, not
           application/json, so the body is what must be judged - not the header.
           Parsing it is the test: a preview session can hand back an HTML page
           before a cart exists, and that must read as a failure, not as an
           empty cart. */
        return r.text().then(function (t) {
          if (!t || t.charAt(0) !== '{') throw new Error('cart api returned non-JSON');
          return JSON.parse(t);
        });
      })
      .then(function (c) { cartFailures = 0; return c; })
      .catch(function (e) { cartFailures++; throw e; });
  }

  function money(cents) {
    try {
      return (cents / 100).toLocaleString(undefined, { style: 'currency',
        currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD' });
    } catch (e) { return '$' + (cents / 100).toFixed(2); }
  }

  /* A line's identity across polls. The signed print-ready URL is deliberately
     NOT part of it, because BAGS regenerates that. */
  function lineKey(i) {
    var p = i.properties || {};
    return [i.variant_id, i.quantity, String(p['Uploaded File'] || ''), i.key].join('|');
  }

  function baseName(u) { return String(u || '').split('/').pop().split('?')[0]; }

  /* Does this line look like the sheet we just asked BAGS to add?
       - new since the snapshot taken before the click
       - carries BAGS' own properties: uploaded file, print-ready file, DPI
       - its uploaded filename ENDS WITH the file we handed BAGS (BAGS prefixes
         a timestamp, so equality would never match)
     Presence of _Print Ready File is checked, never its value. */
  function matchesExpected(line, exp) {
    var p = line.properties || {};
    if (!p['Uploaded File'] || !p['_Print Ready File']) return false;
    if (p['File DPI'] === undefined || p['File DPI'] === null) return false;
    if (exp.fileName) {
      var got = baseName(p['Uploaded File']);
      if (got.slice(-exp.fileName.length) !== exp.fileName) return false;
    }
    if (exp.quantity && line.quantity !== exp.quantity) return false;
    return true;
  }

  function findExpectedLine(c, exp) {
    var fresh = c.items.filter(function (i) { return exp.before.indexOf(lineKey(i)) === -1; });
    var hit = fresh.filter(function (i) { return matchesExpected(i, exp); });
    if (hit.length === 1) return { line: hit[0], newLines: fresh.length };
    if (hit.length > 1) return { line: hit[hit.length - 1], newLines: fresh.length, ambiguous: true };
    return null;
  }

  /* ----------------------------------------------------------------- UI --- */
  function ensureSuccess() {
    if (successEl && document.body.contains(successEl)) return successEl;
    var d = document.createElement('div');
    d.className = 'up-ord';
    d.setAttribute('data-up-order', '');
    d.hidden = true;
    d.innerHTML =
      '<div class="up-ord__card" role="status" aria-live="polite">' +
        '<div class="up-ord__tick" aria-hidden="true">&#10003;</div>' +
        '<div class="up-ord__main">' +
          '<p class="up-ord__title" data-ord-title></p>' +
          '<p class="up-ord__meta" data-ord-meta></p>' +
        '</div>' +
        '<div class="up-ord__acts">' +
          '<button type="button" class="up-ord__btn up-ord__btn--primary" data-ord-another></button>' +
          '<a class="up-ord__btn" data-ord-view href="/cart"></a>' +
        '</div>' +
        '<button type="button" class="up-ord__link" data-ord-keep></button>' +
      '</div>';
    var wrap = document.querySelector(SEL.wrapper) || document.querySelector(SEL.host);
    if (wrap && wrap.parentNode) wrap.parentNode.insertBefore(d, wrap.nextSibling);
    else document.body.appendChild(d);
    d.querySelector('[data-ord-another]').textContent = COPY.another;
    d.querySelector('[data-ord-view]').textContent = COPY.view;
    d.querySelector('[data-ord-keep]').textContent = COPY.keep;
    d.querySelector('[data-ord-another]').addEventListener('click', addAnother);
    d.querySelector('[data-ord-view]').addEventListener('click', function () {
      guard.allowNext = true; disarm();
    });
    d.querySelector('[data-ord-keep]').addEventListener('click', function () { d.hidden = true; });
    successEl = d;
    return d;
  }

  function ensureSummary() {
    if (summaryEl && document.body.contains(summaryEl)) return summaryEl;
    var d = document.createElement('div');
    d.className = 'up-ordsum';
    d.setAttribute('data-up-order-summary', '');
    d.hidden = true;
    d.innerHTML =
      '<span class="up-ordsum__label">' + COPY.yourOrder + '</span>' +
      '<span class="up-ordsum__val" data-sum-val></span>' +
      '<a class="up-ordsum__link" href="/cart" data-sum-view>' + COPY.view + '</a>';
    var wrap = document.querySelector(SEL.wrapper) || document.querySelector(SEL.host);
    if (wrap && wrap.parentNode) wrap.parentNode.insertBefore(d, wrap);
    else document.body.appendChild(d);
    d.querySelector('[data-sum-view]').addEventListener('click', function () {
      guard.allowNext = true; disarm();
    });
    summaryEl = d;
    return d;
  }

  function paintSummaryAt(gen) {
    return function (c) { if (!stale(gen)) paintSummary(c); };
  }

  function paintSummary(c) {
    var d = ensureSummary();
    if (!c || !c.item_count) { d.hidden = true; return; }
    d.querySelector('[data-sum-val]').textContent =
      (c.item_count === 1 ? COPY.summaryOne : COPY.summaryMany.replace('{n}', c.item_count)) +
      '  ·  ' + money(c.total_price);
    d.hidden = false;
  }

  /* Never claim success we did not observe. If we could not read the cart we
     say so and point at the order, instead of silently doing nothing. */
  function showUnconfirmed() {
    var d = ensureSuccess();
    d.querySelector('[data-ord-title]').textContent = COPY.unconfirmed;
    d.querySelector('[data-ord-meta]').textContent = COPY.unconfirmedMeta;
    d.setAttribute('data-up-unconfirmed', '');
    d.hidden = false;
    try { d.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (e) {}
  }

  function showSuccess(line, c) {
    var d0 = ensureSuccess();
    d0.removeAttribute('data-up-unconfirmed');
    var d = ensureSuccess();
    d.querySelector('[data-ord-title]').textContent = COPY.added;
    var bits = [];
    if (line.variant_title) bits.push(line.variant_title);
    bits.push('Qty ' + line.quantity);
    bits.push(money(line.line_price));
    d.querySelector('[data-ord-meta]').textContent = bits.join('  ·  ');
    d.hidden = false;
    paintSummary(c);
    try { d.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (e) {}
    var b = d.querySelector('[data-ord-another]');
    setTimeout(function () { try { b.focus(); } catch (e) {} }, 60);
  }

  /* --------------------------------------------------- add another sheet -- */
  function addAnother() {
    log('resetting for another sheet');
    if (successEl) successEl.hidden = true;
    var rm = document.querySelector(SEL.removeButton);
    if (rm) { try { rm.click(); } catch (e) {} }
    var input = document.querySelector(SEL.fileInput);
    if (input) { try { input.value = ''; } catch (e) {} }
    /* The next file to appear in the input is one the customer has just chosen
       - but only once we have observed the input actually empty. */
    freshFile = false;
    sawEmptyInput = false;
    blockedClicks = 0;
    readyMemo = { t: 0, v: null };
    bumpGen('add another sheet');
    if (window.upPreflight && window.upPreflight.resetForNewSheet) {
      window.upPreflight.resetForNewSheet();
    }
    var q = document.querySelector(SEL.quantity);
    if (q && 'value' in q) {
      q.value = 1;
      try { q.dispatchEvent(new Event('change', { bubbles: true })); } catch (e) {}
    }
    state = S.READY;
    expected = null;
    paintButton();
    disarm();
    reenableWhenReady();
    var target = document.querySelector(SEL.wrapper) || document.querySelector(SEL.host);
    if (target) { try { target.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (e) {} }
    cart().then(paintSummaryAt(currentGen())).catch(function () {});
  }

  /* ------------------------------------------------------------ the flow -- */
  function isCartButton(t) { return !!(t && t.closest && t.closest(SEL.cartButton)); }

  function backToReady(why) {
    if (state === S.SUCCESS) return;
    state = S.READY;
    paintButton();
    disarm();
    log('back to ready:', why);
  }

  document.addEventListener('click', function (e) {
    if (!isCartButton(e.target)) return;

    if (state === S.ATTEMPTING || state === S.WAITING) {
      e.preventDefault(); e.stopPropagation(); e.stopImmediatePropagation();
      log('swallowed a repeat click');
      return;
    }

    if (state === S.READY && preparing()) {
      if (blockedClicks < BLOCKED_CLICK_LIMIT) {
        blockedClicks++;
        e.preventDefault(); e.stopPropagation(); e.stopImmediatePropagation();
        announce(COPY.stillPreparing);
        log('held a click while the sheet was still uploading', blockedClicks);
        return;
      }
      log('letting an insisted click through; BAGS decides');
    }
    // our own gate has priority; if it is holding, let it handle the click
    if (window.upPreflight && window.upPreflight.state) {
      var st = window.upPreflight.state();
      if (st && !st.acknowledged) return;
    }

    state = S.ATTEMPTING;
    paintButton();
    arm();
    var gen = currentGen();
    var fp = bagsFingerprint();
    var q = document.querySelector(SEL.quantity);
    var qty = q && q.value ? (parseInt(q.value, 10) || null) : null;
    cart().then(function (c) {
      if (stale(gen)) return;
      expected = { before: c.items.map(lineKey), beforeCount: c.item_count,
                   fileName: fp.name || null, quantity: qty,
                   fingerprint: fp, t0: Date.now(), gen: gen };
    }).catch(function () {
      if (stale(gen)) return;
      expected = { before: [], beforeCount: -1, fileName: fp.name || null,
                   quantity: qty, fingerprint: fp, t0: Date.now(), gen: gen };
    }).then(function () {
      if (state === S.ATTEMPTING || state === S.WAITING) { state = S.WAITING; poll(); }
    });

    // release the physical debounce quickly; correctness is the state machine
    setTimeout(function () { if (state === S.ATTEMPTING) state = S.WAITING; }, DEBOUNCE_MS);
  }, true);

  /* Poll the cart for the SPECIFIC expected line. Backs off after the first
     couple of seconds, and hands control back as soon as we can tell the add
     did not happen - never on a long blind timeout. */
  function poll() {
    var started = expected ? expected.t0 : Date.now();
    (function tick() {
      var age = Date.now() - started;
      if (age > POLL_MAX_MS) {
        backToReady('poll window closed');
        showUnconfirmed();
        return;
      }
      var gap = age < 2000 ? 400 : (age < 8000 ? 800 : 1500);
      setTimeout(function () {
        cart().then(function (c) {
          if (!expected || state === S.SUCCESS) return;
          if (stale(expected.gen)) { log('abandoning a poll for a replaced sheet'); return; }
          var found = findExpectedLine(c, expected);
          if (found) {
            state = S.SUCCESS;
            paintButton();
            disarmAfterSuccess();
            lastAdded = expected.fingerprint;
            freshFile = false;
            log('expected line confirmed' + (found.ambiguous ? ' (ambiguous, took latest)' : ''));
            showSuccess(found.line, c);
            return;
          }
          /* Not there yet. If BAGS is visibly refusing - its own unticked
             acknowledgement, or an error - hand control straight back so the
             customer can fix it and click again immediately. */
          if (age > REFUSAL_PROBE_MS && c.item_count === expected.beforeCount &&
              bagsRefusing()) {
            backToReady('BAGS refused');
            return;
          }
          tick();
        }).catch(tick);
      }, gap);
    }());
  }

  /* Which vendor signals are visible right now, and which are not. This is the
     first thing to look at if sheet 2 stops becoming addable: it separates "the
     customer has not finished uploading" from "the vendor renamed something". */
  function adapterReport() {
    var card = document.querySelector(SEL.card);
    var det = document.querySelector(SEL.detection);
    var host = document.querySelector(SEL.host);
    var c = txt(card), d = txt(det);
    return {
      version: '2.1.0',
      elements: {
        cartButton: !!document.querySelector(SEL.cartButton),
        fileInput: !!document.querySelector(SEL.fileInput),
        uploadCard: vis(card),
        detection: vis(det),
        host: !!host
      },
      probes: (function () {
        var out = { fileNamed: FILE_IN_CARD.test(c) };
        Object.keys(PROBE).forEach(function (k) {
          var against = (k === 'uploadComplete' || k === 'uploadFailed') ? c : d;
          out[k] = PROBE[k].test(against);
        });
        return out;
      }()),
      sampled: { card: c.slice(0, 90), detection: d.slice(0, 120) },
      verdict: bagsReady(false),
      failsClosed: true
    };
  }

  function bagsRefusing() {
    var host = document.querySelector(SEL.host);
    if (!host) return true;
    var errs = [].slice.call(host.querySelectorAll('[class*=error],[class*=invalid]'))
      .filter(vis).map(txt).filter(Boolean);
    if (errs.length) return true;
    var open = [].slice.call(host.querySelectorAll('input[type=checkbox]'))
      .filter(function (c) { return vis(c) && !c.checked; });
    if (open.length) return true;
    return !bagsReady(false).ok;
  }

  /* ------------------------------------------------- button state model --
     Three visually distinct states. BAGS draws its own spinner inside the
     button; because we hide its text and draw ours, the label has to follow our
     state or the customer sees a spinner beside an unchanged "Add sheet to
     order" and reads it as "still processing" when it is actually ready. */
  function paintButton() {
    if (CFG.relabelCartButton === false) return;
    var b = document.querySelector(SEL.cartButton);
    if (!b) return;
    var label, ui;
    if (state === S.SUCCESS) { label = COPY.addedShort; ui = 'success'; }
    else if (state === S.ATTEMPTING || state === S.WAITING) { label = COPY.adding; ui = 'adding'; }
    else if (hasFile() && !bagsReadyCached().ok) { label = COPY.preparing; ui = 'uploading'; }
    else { label = COPY.relabel; ui = 'ready'; }
    if (b.getAttribute('data-up-label') !== label) b.setAttribute('data-up-label', label);
    if (b.getAttribute('data-up-state') !== ui) b.setAttribute('data-up-state', ui);
    /* aria-disabled, not disabled: the button keeps its place in the tab order
       and still announces itself, but assistive tech says it is not actionable
       yet. Cleared the moment the sheet is ready. */
    if (ui === 'uploading') b.setAttribute('aria-disabled', 'true');
    else if (b.getAttribute('aria-disabled') === 'true' && !b.disabled) {
      b.removeAttribute('aria-disabled');
    }
  }
  var relabel = paintButton;

  try {
    new MutationObserver(function () {
      noteFileState();
      if (state === S.READY && hasFile()) ensureWatching();
      relabel();
    }).observe(document.querySelector(SEL.host) || document.body,
               { childList: true, subtree: true });
  } catch (e) {}
  /* A file chosen through the native dialog or dropped on the uploader: either
     way the input ends up holding it, and this is how we learn the sheet on
     screen is a new one. Capture phase, so no BAGS handler can stop it first. */
  document.addEventListener('change', function (e) {
    if (e.target && e.target.matches && e.target.matches(SEL.fileInput)) {
      if (e.target.files && e.target.files.length) noteNewChoice('the file dialog');
      noteFileState();
      ensureWatching();
      relabel();
    }
  }, true);

  document.addEventListener('drop', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    if (!t.closest(SEL.host) && !t.closest(SEL.wrapper)) return;
    var dt = e.dataTransfer;
    if (!dt || !dt.files || !dt.files.length) return;
    noteNewChoice('drag and drop');
    ensureWatching();
    relabel();
  }, true);
  relabel();
  ensureWatching();
  cart().then(paintSummaryAt(currentGen())).catch(function () {});

  window.upOrder = {
    version: '2.0.0',
    guard: function () {
      return { supported: guard.supported, armed: guard.armed,
               grace: guard.grace, blocked: guard.blocked,
               failures: guard.failures, cartApiFailures: cartFailures };
    },
    state: function () { return state; },
    expected: function () { return expected; },
    bagsReady: function () { return bagsReady(false); },
    reenableReason: function () { return reenableWhenReady._last || null; },
    fingerprint: bagsFingerprint,
    buttonState: function () {
      var b = document.querySelector(SEL.cartButton);
      return b ? { label: b.getAttribute('data-up-label'),
                   ui: b.getAttribute('data-up-state'),
                   disabled: !!b.disabled,
                   loader: (function () {
                     var l = b.querySelector('.gs-loader');
                     return l ? getComputedStyle(l).display : 'none';
                   }()) } : null;
    },
    lastAdded: function () { return lastAdded; },
    freshFile: function () { return freshFile; },
    generation: function () { return sheetGen; },
    adapter: adapterReport,
    preparing: preparing,
    blockedClicks: function () { return blockedClicks; },
    readyFresh: function () { return bagsReady(true); },
    addAnother: addAnother,
    refreshSummary: function () { return cart().then(paintSummaryAt(currentGen())); }
  };
}());
